import { Card } from "@components/ui-kit";
import { CommissionUnit } from "@src/__generated__/urql-graphql";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import { forwardRef, useImperativeHandle, useRef } from "react";
import type { ExpenseBudgetForm, OurWorkBudgetForm } from "../forms";
import { ExpenseCategory, OurWorkCategory } from "../models";
import CategoryHeader from "./CategoryHeader";
import CategoryItem from "./CategoryItem";
import CategorySummary from "./CategorySummary";
import { EmptyCategory } from "./EmptyCategory";

interface CategoryProps {
  item: OurWorkCategory | ExpenseCategory;
}

export interface CategoryRefProps {
  /**
   * Return true when invalid form has been found
   */
  scrollToInvalid: () => boolean;
}

const Category = forwardRef<CategoryRefProps, CategoryProps>(function Category(
  { item },
  ref,
) {
  const { workspaceStore } = useStore();

  const myRef = useRef<HTMLElement>(null);

  useImperativeHandle(ref, () => ({
    scrollToInvalid() {
      const budgetForm = (
        item.budgetItems as Array<
          [string, OurWorkBudgetForm | ExpenseBudgetForm]
        >
      ).find(([_, i]) => i._form.hasError);

      if (budgetForm?.[1]._form.hasError) {
        myRef.current?.scrollIntoView();
      }

      return !!budgetForm?.[1]._form.hasError;
    },
  }));

  const onAdd = () => {
    if (item instanceof OurWorkCategory) {
      item.addBudget({
        our_work_budget_category_id: item.budget_category_id,
        total_budget: new NumberModel(
          0,
          workspaceStore.settings!.currency.denomination,
        ),
      });
    } else {
      item.addBudget({
        expense_budget_category_id: item.budget_category_id,
        total_budget: new NumberModel(
          0,
          workspaceStore.settings!.currency.denomination,
        ),
        planned_commission: new NumberModel(
          0,
          workspaceStore.settings!.currency.denomination,
        ),
        commission_unit: CommissionUnit.Absolute,
        planned_expenses: new NumberModel(
          0,
          workspaceStore.settings!.currency.denomination,
        ),
      });
    }
  };

  return (
    <section className="space-y-8" ref={myRef}>
      <Card sectioned>
        <CategoryHeader title={item.name} onAdd={onAdd} />
        <Card.Section>
          {item.budgetItems.length ? (
            (
              item.budgetItems as Array<
                [string, OurWorkBudgetForm | ExpenseBudgetForm]
              >
            ).map(([key, i], index) => (
              <CategoryItem
                isFirst={index === 0}
                key={key}
                item={i}
                onRemove={() => item.removeBudget(key)}
              />
            ))
          ) : (
            <EmptyCategory />
          )}
        </Card.Section>
      </Card>
      {!!item.budgetItems.length && (
        <CategorySummary
          invoiced={item.credit}
          expenses={
            item instanceof ExpenseCategory
              ? item.real_expenses_without_other
              : undefined
          }
          budget={item.total_budget}
        />
      )}
    </section>
  );
});

export default observer<CategoryProps, CategoryRefProps>(Category);
